import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export interface RadioButtonGroupContextValue {
  name: string;
  checkedValue?: string;
  onChange?: (event: React.SyntheticEvent<HTMLInputElement>) => void;
}

const RadioButtonGroupContext = createContext<
  RadioButtonGroupContextValue | undefined
>(undefined);

export const useRadioButtonGroup = ():
  | RadioButtonGroupContextValue
  | undefined => {
  return useContext(RadioButtonGroupContext);
};

export interface RadioButtonGroupProps {
  name: string;
  value?: string;
  /**
   * アンコントロールにする場合の初期値を指定します。`value`が指定されている場合は無視されます。
   */
  defaultValue?: string;
  onChange?: (event: React.SyntheticEvent<HTMLInputElement>) => void;
  children: React.ReactNode;
}
export const RadioButtonGroup = ({
  name,
  children,
  value,
  defaultValue,
  onChange,
}: RadioButtonGroupProps) => {
  const isControlled = value !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const hasDefaultValue = defaultValue !== undefined;

  const checkedValue = isControlled
    ? value
    : hasDefaultValue
      ? uncontrolledValue
      : undefined;

  const handleChange = useCallback(
    (event: React.SyntheticEvent<HTMLInputElement>) => {
      if (!isControlled && hasDefaultValue) {
        setUncontrolledValue(event.currentTarget.value);
      }
      onChange?.(event);
    },
    [isControlled, hasDefaultValue, onChange],
  );

  const contextValue = useMemo(
    () => ({ name, checkedValue, onChange: handleChange }),
    [name, checkedValue, handleChange],
  );

  return (
    <RadioButtonGroupContext.Provider value={contextValue}>
      {children}
    </RadioButtonGroupContext.Provider>
  );
};
